import { getAuthToken } from '@/app-shell/rpa/agent-client/auth-token';
import { getDomains } from '@/helpers/getDomains';
import {
  type GenerateJWTResponse,
  type LinkHeidiAccountResponse,
  type UnlinkHeidiAccountResponse,
  type ValidateOpenAPIAccessResponse,
} from '@/types';
import { openApiHttp } from '@/utils/scribeHTTP';
import { type Region } from '@heidi/schema/ml-scribe';

// `payments/checkout` is a commerce route and now lives in
// `packages/commerce/src/data/api/subscription.ts`. Name-intact re-export so
// existing importers — and the `jest.mock('@/pages/api/scribeOpenAPI', …)`
// literals that reach this module through the pages shim — keep resolving.
export { createOpenApiCheckoutSession } from '@heidi/commerce/data';

export const generateJwt = ({
  apiKey,
  userEmail,
  userId,
  region,
}: {
  apiKey: string;
  userEmail: string;
  userId: string;
  region?: Region;
}) => {
  const baseUrl = region ? getDomains(region)?.openApiBaseUrl : undefined;

  const url = baseUrl
    ? `${baseUrl}jwt?email=${userEmail}&third_party_internal_id=${userId}`
    : `/jwt?email=${userEmail}&third_party_internal_id=${userId}`;

  return openApiHttp.get<GenerateJWTResponse>(url, {
    headers: {
      'Heidi-Api-Key': apiKey,
    },
  });
};

export const linkAccounts = ({
  kindeUserId,
  jwtToken,
  region,
  workspaceId,
}: {
  kindeUserId: string;
  jwtToken?: string;
  region?: Region;
  // Widget-auth workspace picker: re-POSTs the selection against the
  // existing link to rebind the linked account's workspace.
  workspaceId?: string;
}) => {
  const baseUrl = region ? getDomains(region)?.openApiBaseUrl : undefined;
  const url = baseUrl ? `${baseUrl}users/linked-account` : `users/linked-account`;

  // Forward the current user's regular auth0/kinde access token so the backend
  // can validate it and link the authenticated user directly. The body
  // (kinde_user_id) is retained for backward compatibility only.
  const authToken = getAuthToken();

  const headers: Record<string, string> = {};
  if (jwtToken) {
    headers.Authorization = `Bearer ${jwtToken}`;
  }
  if (authToken) {
    headers['X-Auth-Token'] = authToken;
  }

  return openApiHttp.post<LinkHeidiAccountResponse>(
    url,
    {
      kinde_user_id: kindeUserId,
      ...(workspaceId ? { workspace_id: workspaceId } : {}),
    },
    Object.keys(headers).length > 0 ? { headers } : undefined
  );
};

export const getLinkedAccountAccess = () => {
  return openApiHttp.get<ValidateOpenAPIAccessResponse>(`/users/linked-account/access`);
};

export const unlinkEhrUserAccount = () => {
  return openApiHttp.delete<UnlinkHeidiAccountResponse>(`/users/linked-account:unlink`);
};

export const switchLinkedAccountToNewUser = ({
  newKindeUserId,
}: {
  newKindeUserId: string;
}) => {
  return openApiHttp.patch<LinkHeidiAccountResponse>(`/users/linked-account:switch`, {
    new_kinde_user_id: newKindeUserId,
  });
};
